/*
 * Full-width masthead search panel.
 *
 * Shopify's PredictiveSearch custom element remains the data, request-cache,
 * cancellation, live-region and keyboard-selection engine. This file only
 * coordinates the surrounding Madehall panel and its empty-query state.
 */
(function () {
  'use strict';

  var root = document.querySelector('.mh-search');
  if (!root) return;
  if (root.dataset.mhSearchReady === 'true') return;
  root.dataset.mhSearchReady = 'true';

  var toggle = root.querySelector('[data-mh-search-toggle]');
  var panel = root.querySelector('[data-mh-search-panel]');
  var closeButton = root.querySelector('[data-mh-search-close]');
  var form = root.querySelector('[data-mh-search-form]');
  var input = root.querySelector('[data-mh-search-input]');
  var predictive = root.querySelector('[data-mh-search-predictive]');
  if (!toggle || !panel || !form || !input) return;

  // Normalize only the query; Shopify still owns matching and ranking.
  var searchPath = new URL(form.action, window.location.href).pathname;
  var currentUrl = new URL(window.location.href);
  var currentQuery = currentUrl.searchParams.get('q');
  if (currentUrl.pathname === searchPath && currentQuery !== null) {
    var normalizedQuery = currentQuery.trim().toLowerCase();
    if (currentQuery !== normalizedQuery) {
      currentUrl.searchParams.set('q', normalizedQuery);
      window.location.replace(currentUrl.href);
      return;
    }
  }

  document.addEventListener('submit', function (event) {
    var searchForm = event.target;
    if (!(searchForm instanceof HTMLFormElement)) return;
    var actionUrl = new URL(searchForm.action, window.location.href);
    if (actionUrl.origin !== window.location.origin || actionUrl.pathname !== searchPath) return;
    var queryInput = searchForm.elements.namedItem('q');
    if (queryInput && typeof queryInput.value === 'string') {
      queryInput.value = queryInput.value.trim().toLowerCase();
    }
  }, true);

  var OPEN_CLASS = 'mh-search--open';
  var QUERY_CLASS = 'mh-search__predictive--has-query';

  function isOpen() {
    return root.classList.contains(OPEN_CLASS);
  }

  function updatePanelTop() {
    var header = root.closest('.mh-header');
    panel.style.setProperty('--mh-search-panel-top', '0px');
    if (!header || !isOpen()) return;

    var desiredTop = Math.max(0, Math.round(header.getBoundingClientRect().top));
    var renderedTop = Math.round(panel.getBoundingClientRect().top);
    panel.style.setProperty('--mh-search-panel-top', desiredTop - renderedTop + 'px');
    // The fixed panel can be positioned relative to the header's containing block.
    // Its available height must use the actual viewport offset, not the CSS inset.
    panel.style.setProperty('--mh-search-panel-viewport-top', Math.max(0, panel.getBoundingClientRect().top) + 'px');
  }

  function updateQueryState() {
    if (!predictive) return;
    predictive.classList.toggle(QUERY_CLASS, input.value.trim().length > 0);
  }

  function closeNavigation() {
    document.querySelectorAll('[data-mh-mega][open], [data-mh-mobile-group][open], .mh-nav-menu[open]').forEach(function (details) {
      details.removeAttribute('open');
    });
  }

  function openPanel() {
    closeNavigation();
    updateQueryState();
    root.classList.add(OPEN_CLASS);
    updatePanelTop();
    document.documentElement.classList.add('mh-search-open');
    toggle.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');
    window.requestAnimationFrame(function () {
      if (!isOpen()) return;
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    });
  }

  function closePanel(restoreFocus) {
    if (!isOpen()) return;
    root.classList.remove(OPEN_CLASS);
    document.documentElement.classList.remove('mh-search-open');
    toggle.setAttribute('aria-expanded', 'false');
    panel.setAttribute('aria-hidden', 'true');
    if (predictive && typeof predictive.close === 'function') predictive.close();
    if (restoreFocus) toggle.focus();
  }

  toggle.addEventListener('click', function () {
    if (isOpen()) closePanel(true);
    else openPanel();
  });

  if (closeButton) {
    closeButton.addEventListener('click', function () {
      closePanel(true);
    });
  }

  input.addEventListener('input', updateQueryState);
  form.addEventListener('reset', function () {
    window.requestAnimationFrame(updateQueryState);
  });

  document.addEventListener('keydown', function (event) {
    if (!isOpen()) return;
    if (event.key === 'Tab') {
      // Recompute after each results update; hidden default links are not tab stops.
      var focusable = Array.from(panel.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')).filter(function (element) {
        return element.tabIndex >= 0 && !element.matches(':disabled') &&
          !element.closest('[inert]') && element.getClientRects().length > 0 &&
          window.getComputedStyle(element).visibility === 'visible';
      });
      var first = focusable[0] || input;
      var last = focusable[focusable.length - 1] || input;
      if (!panel.contains(document.activeElement) ||
          (event.shiftKey && document.activeElement === first) ||
          (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }
    if (event.key === 'Escape' && isOpen()) {
      event.preventDefault();
      closePanel(true);
    }
  });

  document.addEventListener('click', function (event) {
    if (!isOpen() || panel.contains(event.target) || toggle.contains(event.target)) return;
    closePanel(false);
  });

  window.addEventListener('resize', function () {
    if (isOpen()) updatePanelTop();
  });

  document.addEventListener('shopify:section:unload', function () {
    document.documentElement.classList.remove('mh-search-open');
  });

  root.dataset.mhSearchBound = 'true';
})();
